import { useEffect, useState } from "react";

import {
  CheckCircle2,
  Mail,
  RefreshCw,
  ShieldCheck,
  Loader2,
} from "lucide-react";

import { StepShell } from "@/components/application-form/Field";
import { Button } from "@/components/common/Button";

export function VerifyEmailStep({
  email,
  onVerify,
  onResend,
  busy,
  resendBusy,
}) {
  const [otp, setOtp] = useState("");

  const [countdown, setCountdown] =
    useState(30);

  const [error, setError] =
    useState("");

  /**
   * =====================================================
   * Countdown
   * =====================================================
   */

  useEffect(() => {
    if (countdown <= 0) {
      return;
    }

    const timer =
      setInterval(() => {
        setCountdown(
          (previous) =>
            Math.max(
              previous - 1,
              0
            )
        );
      }, 1000);

    return () =>
      clearInterval(timer);
  }, [countdown]);

  /**
   * =====================================================
   * Verify OTP
   * =====================================================
   */

  async function handleVerify(e) {
    e.preventDefault();

    setError("");

    if (otp.length !== 6) {
      setError(
        "Please enter the 6-digit verification code."
      );

      return;
    }

    try {
      await onVerify(otp);
    } catch (err) {
      setError(
        err?.message ||
          "Unable to verify the code. Please try again."
      );
    }
  }

  /**
   * =====================================================
   * Resend OTP
   * =====================================================
   */

  async function handleResend() {
    if (
      countdown > 0 ||
      resendBusy
    ) {
      return;
    }

    setError("");

    try {
      await onResend();

      /*
       * Clear old OTP
       */
      setOtp("");

      /*
       * Restart countdown
       */
      setCountdown(30);
    } catch (err) {
      setError(
        err?.message ||
          "Unable to resend the verification code."
      );
    }
  }

  /**
   * =====================================================
   * Render
   * =====================================================
   */

  return (
    <StepShell
      title="Verify Your Email"
      description="We have sent a 6-digit verification code to your email address."
    >
      <div className="max-w-md mx-auto">

        {/* Email icon */}

        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 border border-amber-100">
            <Mail className="h-8 w-8 text-[#F5A623]" />
          </div>
        </div>

        {/* Email */}

        <div className="mt-5 text-center">
          <p className="text-sm text-slate-500">
            Verification code sent to
          </p>

          <p className="mt-1 font-semibold text-slate-900 break-all">
            {email || "your email address"}
          </p>
        </div>

        {/* OTP */}

        <form
          onSubmit={handleVerify}
          className="mt-7"
        >
          <label
            htmlFor="email-verification-otp"
            className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2 text-center"
          >
            Enter Verification Code
          </label>

          <input
            id="email-verification-otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={otp}
            onChange={(e) => {
              const value =
                e.target.value
                  .replace(/\D/g, "")
                  .slice(0, 6);

              setOtp(value);
              setError("");
            }}
            placeholder="000000"
            disabled={busy}
            className="
              w-full
              h-14
              rounded-xl
              border
              border-slate-200
              bg-white
              text-center
              text-2xl
              font-bold
              tracking-[0.5em]
              text-slate-900
              outline-none
              transition
              focus:border-[#F5A623]
              focus:ring-4
              focus:ring-amber-500/10
              disabled:bg-slate-100
              disabled:cursor-not-allowed
            "
            data-testid="email-verification-otp"
          />

          {error && (
            <p
              className="mt-3 text-sm text-red-600 text-center"
              data-testid="email-verification-error"
            >
              {error}
            </p>
          )}

          {/* Verify */}

          <Button
            type="submit"
            className="w-full mt-5 justify-center"
            disabled={
              busy ||
              otp.length !== 6
            }
            data-testid="verify-email-btn"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />

                Verifying...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4" />

                Verify Email
              </>
            )}
          </Button>
        </form>

        {/* Resend */}

        <div className="mt-6 text-center">
          {countdown > 0 ? (
            <p
              className="text-sm text-slate-500"
              data-testid="resend-countdown"
            >
              Didn't receive the code?
              {" "}
              Resend available in{" "}

              <span className="font-bold text-slate-900">
                {countdown}s
              </span>
            </p>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={
                resendBusy ||
                busy
              }
              className="
                inline-flex
                items-center
                gap-2
                text-sm
                font-semibold
                text-amber-600
                hover:text-amber-700
                disabled:opacity-50
                disabled:cursor-not-allowed
              "
              data-testid="resend-email-otp-btn"
            >
              {resendBusy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />

                  Sending...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />

                  Resend OTP
                </>
              )}
            </button>
          )}
        </div>

        {/* Security */}

        <div className="mt-7 flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <ShieldCheck className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />

          <p className="text-xs leading-relaxed text-slate-500">
            Your verification code is valid for 10 minutes.
            Never share your OTP with anyone.
          </p>
        </div>
      </div>
    </StepShell>
  );
}